import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject} from '@angular/core';
import {DraftsService, PatientDraft} from 'src/app/core/services/drafts.service';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Observable, Subscription} from 'rxjs';
import {CoreService} from 'src/app/core/services/core.service';
import {debounceTime, distinctUntilChanged, filter, map, switchMap, tap} from 'rxjs/operators';
import {AppMatAutocompleteComponent} from '@xmedical/ui';
import {MedicationItemAddCardComponent} from '@xmedical/clinical-shared';
import {MatDialog} from '@angular/material/dialog';
import {DraftInteractionBehaviour} from 'src/app/core/layout/draft-overlay/draft-interaction-behaviour';
import {Script, Medication, MasterDataRepositoryService, MedicineLibraryProductMatch, libraryProductDescription} from '@xmedical/core';
import {MedicationSearchEntry} from '@xmedical/shared';
import {PatientMedicationStore, PatientScriptsStore} from '../../../core/state';

@Component({
    selector: 'app-create-script',
    templateUrl: './create-script.component.html',
    styleUrls: ['./create-script.component.scss'],
    standalone: false
})
export class CreateScriptComponent implements OnInit, OnDestroy, DraftInteractionBehaviour {

  @Input() draft: PatientDraft;
  form: UntypedFormGroup = null;
  script: Script = null;
  loading = false;
  saving = false;
  // True while a medication from "add new" is being saved to the practice list. It goes on the
  // script only once that save returns, so Save waits for it.
  addingMedication = false;
  @ViewChild(AppMatAutocompleteComponent) autocomplete: AppMatAutocompleteComponent;
  private subscriptions: Subscription[] = [];
  private triggerTextChanges = false;
  // A change made while a save is in flight is not dropped: it is saved again once that save finishes.
  private changedWhileSaving = false;
  private lockRequestedWhileSaving = false;
  private debounceTimeOut = 1500;

  private scriptsStore = inject(PatientScriptsStore);
  private medicationStore = inject(PatientMedicationStore);

  constructor(
    private dialog: MatDialog,
    private draftService: DraftsService,
    private formBuilder: UntypedFormBuilder,
    private coreService: CoreService,
    private masterDataRepository: MasterDataRepositoryService,
  ) {
  }

  // "Add new medication" saves to the practice's medication list, which the server only allows
  // with Clinical.Medication.Write. Without it the search only offers existing medications.
  get canAddNewMedication(): boolean {
    return this.coreService.hasPermission('Clinical.Medication.Write');
  }

  get saveButtonEnabled(): boolean {
    return this.formValid && !this.saving && !this.addingMedication;
  }

  get formValid(): boolean {
    return this.form && this.form.valid;
  }

  get isEditing(): boolean {
    return this.draft.draftId !== null && this.script !== null;
  }

  get hasMedication(): boolean {
    return this.medication.length > 0;
  }

  get medication(): Medication[] {
    return this.script.medicationList;
  }

  // v2 search: the practice list, plus the medicine library when the practice has it on (#333). Picking a
  // library product saves it to the practice list, so without Clinical.Medication.Write only the practice
  // list is offered, as with "add new".
  searchMedication$ = (query: string): Observable<MedicationSearchEntry[]> => {
    return this.medicationStore.search(query, this.canAddNewMedication);
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      patientName: ['', Validators.required],
      scriptDate: [new Date(), Validators.required],
      medicalAidName: [''],
      medicalAidNumber: [''],
    });
    this.getScriptDetails();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  onAddNewItem(searchTerm: string) {
    this.openAddCard(searchTerm, null);
  }

  // A library product: the "add new" card with the product's name and strength filled in, saved to the
  // practice list linked to the product. The script line keeps the productId.
  onLibraryProductSelected(product: MedicineLibraryProductMatch) {
    this.autocomplete.cancelSearch();
    this.openAddCard(libraryProductDescription(product), product.productId);
  }

  private openAddCard(searchTerm: string, productId: number | null) {
    const dialogRef = this.dialog.open(MedicationItemAddCardComponent, {
      panelClass: 'mat-confirmation-dialog-responsive',
      width: '700px',
      data: {
        searchTerm,
        diagnosisCodeSearch: (q: string) => this.masterDataRepository.searchDiagnosisCodes(q),
      },
    });

    // Same as the medication widget: the card returns the form value with no id. Save it to the
    // practice's medication list first, then add it to this script.
    this.subscriptions.push(dialogRef.afterClosed().pipe(
      filter((v): v is Medication => !!v),
      // Only a library pick carries a link; free text from "add new" is saved exactly as entered.
      map(medication => productId === null ? medication : ({...medication, productId} as Medication)),
      tap(() => this.addingMedication = true),
      switchMap(medication => this.medicationStore.saveToPracticeList(medication)),
    ).subscribe({
      next: medication => {
        this.addingMedication = false;
        this.onItemSelected(medication);
      },
      error: error => {
        this.addingMedication = false;
        this.coreService.showErrorToast('Could not add medication');
        console.error(error);
      }
    }));
  }

  medicationItemChanged() {
    this.formChanged();
  }

  onItemSelected(medication: Medication) {
    this.script.medicationList.push(medication);
    this.autocomplete.cancelSearch();
    this.formChanged();
  }

  save() {
    if (this.addingMedication) {
      return;
    }
    this.formChanged(true);
  }

  getScriptDetails() {
    this.loading = true;
    this.subscriptions.push(this.scriptsStore.load(this.draft.draftId).subscribe({
      next: script => {
        this.script = script;
        this.dataLoaded();
      },
      error: error => {
        console.error(error);
      }
    }));
  }


  confirmationMessage(item: Medication): string {
    return 'Are you sure you want to remove ' + item.description + ' this script?';
  }

  public removeMedicationFromScript(index: number) {
    this.script.medicationList.splice(index, 1);
    this.formChanged();
  }

  private dataLoaded(): void {
    this.handleExistingScriptDataLoaded();
    this.subscribeToFormChanges();
    this.loading = false;
  }

  private handleExistingScriptDataLoaded(): void {
    this.form.get('patientName').setValue(this.script.patientName);
    this.form.get('scriptDate').setValue(this.script.scriptDate);
    this.form.get('medicalAidName').setValue(this.script.medicalAidName);
    this.form.get('medicalAidNumber').setValue(this.script.medicalAidNumber);
    this.form.updateValueAndValidity();
    this.triggerTextChanges = true;
  }

  private subscribeToFormChanges(): void {
    this.subscriptions.push(this.form.valueChanges.pipe(debounceTime(this.debounceTimeOut), distinctUntilChanged()).subscribe(() => {
      this.formChanged();
    }));
  }

  private formChanged(locked: boolean = false) {
    if (this.saving) {
      this.changedWhileSaving = true;
      if (locked) {
        this.lockRequestedWhileSaving = true;
      }
      return;
    }
    if (this.form.valid) {
      this.updateScript(locked);
    }
  }

  private saveFinished(): void {
    this.saving = false;
    if (!this.changedWhileSaving) {
      return;
    }
    const locked = this.lockRequestedWhileSaving;
    this.changedWhileSaving = false;
    this.lockRequestedWhileSaving = false;
    this.formChanged(locked);
  }

  private updateScript(locked: boolean): void {
    const updateScript = this.form.value as Script;
    const scriptDate = this.form.get('scriptDate').value;
    updateScript.scriptDate = new Date(scriptDate);
    updateScript.medicationList = this.script.medicationList;
    updateScript.locked = locked;
    updateScript.duration = 0;
    updateScript.scriptType = this.script.scriptType;
    this.saving = true;
    this.subscriptions.push(this.scriptsStore.update(this.script.id, updateScript).subscribe({
      next: () => {
        if (updateScript.locked) {
          this.saving = false;
          this.draftService.closeDraft(this.draft.draftId, this.draft.draftType);
          return;
        }
        this.saveFinished();
      },
      error: error => {
        console.error(error);
        this.saveFinished();
      }
    }));
  }
}
